<script setup lang="ts">
import { computed, useSlots } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import {
  ButtonColor,
  ButtonSize,
  ButtonType,
  ButtonVariant,
} from '../buttons.constants';
import {
  baseClass,
  colorClass,
  iconSizeForButton,
  sizeClass,
} from '../buttons.styles';
import Icon from '../../icon/icon.vue';
import { IconName, iconSizePx } from '../../icon/icon.constants';
import { useLink } from '../../../composables/use-link';

type Props = {
  /**
   * Text label rendered inside the button. When the default slot is used the
   * slot content replaces the visible text and `label` becomes the accessible
   * name (`aria-label`).
   */
  label: string;
  /** Size of the button, controlling its height and font size. */
  size?: ButtonSize;
  /** Visual style variant (filled, outlined, or text). */
  variant?: ButtonVariant;
  /** Semantic color applied according to the chosen `variant`. */
  color?: ButtonColor;
  /** Whether the button expands to fill the full width of its container. */
  block?: boolean;
  /**
   * Native button `type` attribute. Applied only when the button renders as a
   * `<button>`; defaults to `ButtonType.BUTTON` when neither `to` nor `href` is set.
   */
  type?: ButtonType;
  /**
   * Router target. When set, the button renders as the host app's link
   * component (NuxtLink or RouterLink). Takes precedence over `href`.
   */
  to?: RouteLocationRaw;
  /**
   * External/plain URL. When set (and `to` is not), the button renders as an
   * `<a>` element.
   */
  href?: string;
  /**
   * Id of the `<form>` this button is associated with, forwarded to the
   * underlying element's native `form` attribute.
   */
  form?: string;
  /**
   * Glyph rendered before the label. For icon-only buttons use the
   * `IconButton` component. Ignored when `leftImageUrl` is set.
   */
  leftIconName?: IconName;
  /**
   * Image (e.g. a brand logo) rendered before the label in place of the left
   * icon, sized like the icon would be. Takes precedence over `leftIconName`.
   */
  leftImageUrl?: string;
  /** Glyph rendered after the label. */
  rightIconName?: IconName;
};

const props = withDefaults(defineProps<Props>(), {
  size: ButtonSize.MD,
  variant: ButtonVariant.FILLED,
  color: ButtonColor.PRIMARY,
});

// Host app supplies the link component (NuxtLink in app, RouterLink in admin).
const linkComponent = useLink();

const slots = useSlots();

const iconSize = computed(() => iconSizeForButton[props.size]);
const iconPx = computed(() => iconSizePx[iconSize.value]);

const labelClass: Record<ButtonSize, string> = {
  [ButtonSize.XS]: 'text-xs',
  [ButtonSize.SM]: 'text-sm',
  [ButtonSize.MD]: 'text-sm',
  [ButtonSize.LG]: 'text-base',
};

const gapClass: Record<ButtonSize, string> = {
  [ButtonSize.XS]: 'gap-sm',
  [ButtonSize.SM]: 'gap-sm',
  [ButtonSize.MD]: 'gap-md',
  [ButtonSize.LG]: 'gap-md',
};

const btnClass = computed(() => {
  const classes = [
    ...baseClass,
    sizeClass[props.size],
    labelClass[props.size],
    colorClass[props.variant][props.color],
    'rounded-md px-2xl font-bold',
  ];

  if (props.leftIconName || props.leftImageUrl || props.rightIconName) {
    classes.push(gapClass[props.size]);
  }

  if (props.block) {
    classes.push('w-full');
  }

  return classes;
});

const tag = computed(() => {
  if (props.to !== undefined) {
    return linkComponent;
  }

  if (props.href !== undefined) {
    return 'a';
  }

  return 'button';
});

const parsedType = computed<ButtonType | null>(() => {
  if (props.type) {
    return props.type;
  }
  if (props.to === undefined && props.href === undefined) {
    return ButtonType.BUTTON;
  }

  return null;
});
</script>

<template>
  <component
    :is="tag"
    :to="to"
    :type="parsedType"
    :href="href"
    :form="props.form"
    :aria-label="slots.default ? label : undefined"
    :class="btnClass"
  >
    <slot />
    <img
      v-if="leftImageUrl"
      :src="leftImageUrl"
      :width="iconPx"
      :height="iconPx"
      alt=""
      class="shrink-0 object-contain"
    />
    <Icon v-else-if="leftIconName" :name="leftIconName" :size="iconSize" />
    <slot :size="iconSize" :px="iconPx">{{ label }}</slot>
    <Icon v-if="rightIconName" :name="rightIconName" :size="iconSize" />
  </component>
</template>
